import React, { PureComponent } from 'react';
import memoizeOne from 'memoize-one';
import memoize from 'lodash/memoize';
import cn from 'classnames';
import { connect } from 'react-redux';
import { DateTime, DateTimeOptions, Settings } from 'luxon';
import { Button2, Text2 } from '@el8/vital';
import { Box } from 'components';
import { getBookingInfo } from 'modules/booking';
import { logEvent } from 'utils/amplitude';

import { LuxonFormats, State } from 'types';
import styles from './NextAvailabilities.less';

type NextAvailabilitiesOwnProps = {
  availabilities: { [key: string]: string[] };
  showMoreAvailabilities: (firstActiveDate: string) => void;
  selectAvailability: (date: string, time: string, timezone: string) => void;
  state: string;
};

type StateProps = {
  selectedTimezone: string;
};

type NextAvailabilitiesProps = NextAvailabilitiesOwnProps & StateProps;

const mapStateToProps = (state: State): StateProps => {
  const { timezone } = getBookingInfo(state);
  return { selectedTimezone: timezone };
};

const getAvailabilityLabel = memoize(
  (availability: string, short?: boolean): string => {
    const avalabilityDate = DateTime.fromISO(availability, { zone: Settings.defaultZone });
    const currentDay = DateTime.fromObject({
      hour: 0,
      minute: 0,
      second: 0,
      zone: Settings.defaultZone,
    });
    const { days } = avalabilityDate.startOf('day').diff(currentDay, 'days');
    const showYear = avalabilityDate.year !== currentDay.year;

    if (avalabilityDate.hasSame(currentDay, 'day')) {
      return 'Today';
    }
    if (days === 1) {
      return 'Tomorrow';
    }
    return avalabilityDate.toFormat(
      `${short ? 'ccc' : 'cccc'}, ${short ? 'LLL' : 'LLLL'} d${showYear ? ', y' : ''}`,
    );
  },
  (arg1, arg2): string => `${arg1}-${arg2}`,
);

const getCurrentMonthDate = memoizeOne((date: string): string =>
  DateTime.fromISO(date, {
    zone: Settings.defaultZone,
  })
    .startOf('month')
    .toISODate(),
);

class NextAvailabilities extends PureComponent<NextAvailabilitiesProps> {
  isMobile: boolean;

  constructor(props: NextAvailabilitiesProps) {
    super(props);

    this.isMobile = document.documentElement.clientWidth < 500;
  }

  handleMoreClick = (): void => {
    const { availabilities, showMoreAvailabilities } = this.props;
    const today = DateTime.fromObject({}).toISODate();
    const currentMonth = getCurrentMonthDate(Object.keys(availabilities)[0] || today);
    showMoreAvailabilities(currentMonth);
    logEvent('more booking options');
  };

  handleSelectAvaialbilitiy = (date: string, time: string, timezone: string) => (): void => {
    this.props.selectAvailability(date, time, timezone);

    const now = DateTime.fromObject({ zone: Settings.defaultZone });
    const dateTime = DateTime.fromFormat(`${date}, ${time}`, `${LuxonFormats.ServerDate}, HH:mm`, {
      zone: this.props.selectedTimezone,
    });
    const { hours } = dateTime.diff(now, ['hours']);

    logEvent('quick-pick selected', {
      'hours in advance': hours.toFixed(2),
      'selected time': dateTime.toLocaleString(DateTime.DATETIME_SHORT as DateTimeOptions),
    });
  };

  render(): JSX.Element {
    const { availabilities, state } = this.props;
    const availabilityDates = Object.keys(availabilities);

    return (
      <Box className={cn(styles.list, styles[state])} align="bottom">
        {availabilityDates.length ? (
          availabilityDates.map(
            (date): JSX.Element => (
              <div className={styles.group} key={date}>
                <div className={styles.label} title={getAvailabilityLabel(date)}>
                  {getAvailabilityLabel(
                    date,
                    /**
                     * We want to be smart about availability group label and show short version in case
                     * there is just one apot in the group or on mobile, in other cases we will show
                     * the long date
                     */
                    availabilities[date].length === 1 || this.isMobile,
                  )}
                </div>
                <Box className={styles.availabilities} align="bottom">
                  {availabilities[date].map(
                    (time): JSX.Element => (
                      <Button2
                        className={styles.slot}
                        key={time}
                        kind="default"
                        label={DateTime.fromFormat(
                          `${date}, ${time}`,
                          `${LuxonFormats.ServerDate}, HH:mm`,
                        ).toLocaleString(DateTime.TIME_SIMPLE as DateTimeOptions)}
                        onClick={this.handleSelectAvaialbilitiy(
                          date,
                          time,
                          this.props.selectedTimezone,
                        )}
                      />
                    ),
                  )}
                </Box>
              </div>
            ),
          )
        ) : (
          <Text2 className={styles.label}>No available appointments this week.</Text2>
        )}
        <div className={styles.moreBackground} />
        <Box className={styles.moreContainer} justify="right">
          <Button2
            className={styles.more}
            data-testid="booking-more-availabilities-button"
            icon={this.isMobile ? 'forward' : undefined}
            kind="ghost"
            label={this.isMobile ? 'More Available Times' : 'More'}
            onClick={this.handleMoreClick}
          />
        </Box>
      </Box>
    );
  }
}

export default connect(mapStateToProps)(NextAvailabilities);
